import { Injectable } from '@angular/core';

import type { RepeatablePendingDropdownResolutionSummary } from './repeatable-group.types';

export type RepeatableReadinessReason =
    | 'ready'
    | 'hydrating'
    | 'pendingDropdowns'
    | 'resolvingDependencies'
    | 'userEdited'
    | 'cleared';

export type RepeatablePendingDropdownReason = 'optionsNotReadyOrFiltered' | 'notResolvedAfterRetry';

export interface RepeatablePendingDropdownDiagnostic {
    readonly rowIndex: number;
    readonly logicalKey: string;
    readonly rawIds: readonly string[];
    readonly isMulti: boolean;
    readonly optionCount: number;
    readonly reason: RepeatablePendingDropdownReason;
    readonly label?: string;
}

export interface RepeatableReadinessState {
    readonly fieldName: string;
    readonly ready: boolean;
    readonly pendingDropdownCount: number;
    readonly rowCount: number;
    readonly updatedAt: number;
    readonly reason?: RepeatableReadinessReason;
    readonly pendingDropdowns?: readonly RepeatablePendingDropdownDiagnostic[];
    readonly latestResolution?: RepeatablePendingDropdownResolutionSummary;
}

export interface RepeatableReadinessSummary {
    readonly total: number;
    readonly ready: number;
    readonly pending: number;
    readonly pendingDropdownCount: number;
}

type RepeatableReadinessResolver = (isReady: boolean) => void;

/**
 * Per-form readiness registry for repeatable groups.
 *
 * The service is provided by ReactiveFormBuilderComponent so child repeatable groups can publish their
 * hydration state without coupling the parent to the visual component implementation.
 */
@Injectable()
export class RepeatableReadinessService {
    private readonly states = new Map<string, RepeatableReadinessState>();
    private readonly waiters = new Map<string, RepeatableReadinessResolver[]>();

    public setState(state: RepeatableReadinessState): void {
        const normalizedState = this.normalizeState(state);

        this.states.set(normalizedState.fieldName, normalizedState);

        if (normalizedState.ready) {
            this.resolveWaiters(normalizedState.fieldName, true);
        }
    }

    public clear(fieldName?: string): void {
        if (fieldName === undefined) {
            this.resolveAllWaiters(false);
            this.states.clear();
            return;
        }

        this.resolveWaiters(fieldName, false);
        this.states.delete(fieldName);
    }

    public getState(fieldName: string): RepeatableReadinessState | null {
        return this.states.get(fieldName) ?? null;
    }

    public getStates(): RepeatableReadinessState[] {
        return Array.from(this.states.values());
    }

    public getPendingDropdowns(fieldName: string): readonly RepeatablePendingDropdownDiagnostic[] {
        return this.states.get(fieldName)?.pendingDropdowns ?? [];
    }

    public getSummary(): RepeatableReadinessSummary {
        const states = this.getStates();
        const ready = states.filter((state) => state.ready).length;
        const pendingDropdownCount = states.reduce((total, state) => total + state.pendingDropdownCount, 0);

        return {
            total: states.length,
            ready,
            pending: states.length - ready,
            pendingDropdownCount,
        };
    }

    public isReady(fieldName: string): boolean {
        return this.states.get(fieldName)?.ready === true;
    }

    public whenReady(fieldName: string, timeoutMs: number): Promise<boolean> {
        if (this.isReady(fieldName)) {
            return Promise.resolve(true);
        }

        return new Promise((resolve) => {
            let isSettled = false;
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const complete = (isReady: boolean): void => {
                if (isSettled) {
                    return;
                }

                isSettled = true;

                if (timeoutId !== null) {
                    clearTimeout(timeoutId);
                }

                this.removeWaiter(fieldName, complete);
                resolve(isReady);
            };
            const waiters = this.waiters.get(fieldName) ?? [];

            waiters.push(complete);
            this.waiters.set(fieldName, waiters);

            if (timeoutMs > 0) {
                timeoutId = setTimeout(() => complete(this.isReady(fieldName)), timeoutMs);
            }
        });
    }

    private normalizeState(state: RepeatableReadinessState): RepeatableReadinessState {
        const pendingDropdowns = [...(state.pendingDropdowns ?? [])];
        const hasPendingDropdownDiagnostics = state.pendingDropdowns !== undefined;
        const pendingDropdownCount = hasPendingDropdownDiagnostics ? pendingDropdowns.length : state.pendingDropdownCount;

        return {
            ...state,
            pendingDropdowns,
            pendingDropdownCount,
            reason: state.reason ?? this.resolveFallbackReason(state.ready, pendingDropdownCount),
            ...(state.latestResolution ? { latestResolution: state.latestResolution } : {}),
        };
    }

    private resolveFallbackReason(ready: boolean, pendingDropdownCount: number): RepeatableReadinessReason {
        if (ready) {
            return 'ready';
        }

        return pendingDropdownCount > 0 ? 'pendingDropdowns' : 'hydrating';
    }

    private removeWaiter(fieldName: string, waiter: RepeatableReadinessResolver): void {
        const waiters = this.waiters.get(fieldName) ?? [];
        const nextWaiters = waiters.filter((item) => item !== waiter);

        if (nextWaiters.length > 0) {
            this.waiters.set(fieldName, nextWaiters);
        } else {
            this.waiters.delete(fieldName);
        }
    }

    private resolveWaiters(fieldName: string, isReady: boolean): void {
        const waiters = this.waiters.get(fieldName) ?? [];

        this.waiters.delete(fieldName);
        waiters.forEach((resolve) => resolve(isReady));
    }

    private resolveAllWaiters(isReady: boolean): void {
        for (const waiters of this.waiters.values()) {
            waiters.forEach((resolve) => resolve(isReady));
        }

        this.waiters.clear();
    }
}
